import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import Npc from './Npc';
import SafeMount from './SafeMount';
import CameraBasisProbe from './CameraBasisProbe';
import { api } from '../services/api';
import { useWorldStore } from '../state/worldStore';
import { withRemoteBody } from '../services/bodyRigs';
import { useCityStore } from '../city/cityStore';

// NpcManager — populates the street with featured-artist residents (Spec: City
// Life). Data-driven: models/avatars/roster.json is the MODEL SET (which artists
// have a rigged body + where they stand); /api/featured-artists/showcase
// (is_featured_artist) supplies live names/genres and validates the roster.
//
// A featured artist with no roster entry is logged as "needs a model" so the
// content pipeline knows what to build next. Roster JSON is fetched SAME-ORIGIN
// (a Vite public asset), not through the API base.
const SHOW_TEST = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('test') === '1';

export default function NpcManager() {
  const [npcs, setNpcs] = useState([]);
  // Bumped to build the list again when the API's bodies did not answer (below).
  const [bodiesTry, setBodiesTry] = useState(0);
  const setNpcList = useWorldStore((s) => s.setNpcList);
  const setFollowing = useWorldStore((s) => s.setFollowing);
  const setOwned = useWorldStore((s) => s.setOwned);
  const placing = useWorldStore((s) => s.adminTool) === 'place';
  // Saved street placements (world_objects, zone 'street'). Read from the STORE
  // rather than a local fetch so an edit in the panel moves the body on the
  // street immediately instead of after a reload.
  const placed = useWorldStore((s) => s.worldObjects);
  // THE LIVING CITY (city/): once the city's stage manager is live, the artists
  // walk their own days (city/CityResidents) and nobody stands on a fixed post.
  // This component keeps LOADING the bodies (`npcList` is what the city draws
  // with); it only stops placing them. An API without the city never goes live,
  // and the street keeps its residents exactly as before.
  const cityLive = useCityStore((s) => s.live && (s.snap?.residents?.length || 0) > 0);
  // The fetch that used to sit here belongs to StreetStream now — one loader for
  // the streamed street kinds, asking by cell instead of pulling every resident
  // in the world. Reading from the store is unchanged, which is what keeps a
  // panel edit moving the body on the street immediately.

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // no-store: on returning to the street this re-reads the CURRENT file
      // (autosaved sizes/positions), not a stale browser-cached copy.
      const roster = await fetch('/models/avatars/roster.json', { cache: 'no-store' })
        .then((r) => r.json())
        .catch(() => ({ npcs: [] }));
      let featured = [];
      try { featured = (await api.featuredArtists())?.artists || []; } catch { /* offline → manifest only */ }

      // Who the viewer follows + owns (for the map's headshot pins). Optional —
      // a signed-out / offline viewer just gets no markers.
      try {
        const follows = (await api.following())?.following || [];
        if (!cancelled) setFollowing(follows.map((f) => f.avatar_id));
      } catch { if (!cancelled) setFollowing([]); }
      try {
        const mine = (await api.myAvatars())?.avatars || [];
        if (!cancelled) setOwned(mine.map((a) => a.avatar_id));
      } catch { if (!cancelled) setOwned([]); }

      const byId = new Map(featured.map((a) => [a.avatar_id, a]));
      const entries = roster.npcs || [];
      const modeledIds = new Set(entries.map((n) => n.avatarId));

      // Enrich each modeled NPC with live name/genre + headshot from the
      // featured list (headshot powers the map's follow markers).
      const merged = entries.map((n) => {
        const a = byId.get(n.avatarId);
        // ⚠ NO "WHERE A MEMBER LEFT THEM" any more. A member used to walk as
        // their artist and the artist stayed where they logged out (the saved
        // `world_x/z`). Nobody walks as an artist now (2026-10-23), so those
        // positions are a puppet's footprints, not the artist's choice — the
        // roster spot (or a placed one, below) stands until the Living City
        // puts them where their own day says.
        return {
          ...n,
          name: a?.name || n.name || `Artist ${n.avatarId}`,
          genre: a?.main_genre || null,
          headshotUrl: a?.profile_image_url || null,
        };
      });

      // Surface featured artists still waiting on a rigged model.
      const needsModel = featured.filter((a) => !modeledIds.has(a.avatar_id));
      if (needsModel.length) {
        console.log(`[NPC] ${merged.length} modeled · ${needsModel.length} featured artist(s) need a model:`,
          needsModel.map((a) => `${a.name} (#${a.avatar_id})`).join(', '));
      }

      // Player-BUILT bodies from the API (the editor's 3D-model flow). They
      // join the store's npcList so every embodiment consumer — Peers, the
      // character select, the in-hand phone gate — resolves them, but they are
      // NOT added to this component's own render list: a paid-for body is a
      // costume the player wears, not a resident standing in the street.
      // The roster wins on collision — a hand-tuned entry stays canon.
      let userModels = [];
      /* ⚠ AND ASKED AGAIN IF THEY DID NOT ANSWER (2026-11-02). Asked once, a
         restarting or briefly-down API left every API-built body — a cast member,
         the heli pilot, a player's paid body — missing for the whole visit, the
         roster standing in (Khari: "I can no longer see the pilot in her seat").
         The roster still draws now; the list is built again shortly. */
      try { userModels = await api.avatarModels(); } catch {
        if (!cancelled && bodiesTry < 6) setTimeout(() => { if (!cancelled) setBodiesTry((n) => n + 1); }, 15000 * (bodiesTry + 1));
      }
      const extras = userModels
        .filter((m) => m.rigged && !modeledIds.has(m.avatarId))
        .map((m) => ({ ...m, playerOnly: true }));
      if (extras.length) console.log(`[NPC] + ${extras.length} player-built bod${extras.length === 1 ? 'y' : 'ies'} (embodiment only)`);

      // THE MODELS MOVED TO THE SERVER (Khari, 2026-08-24). For a roster
      // avatar with an API row, the row's GCS-proxied URLs win for the FILES —
      // that's what lets the deploy stop shipping ~67MB of bodies and lets
      // characters lazy-load later. roster.json stays the METADATA register:
      // post, height, gait tuning and outfits are hand-tuned and keep winning.
      //
      // The file set is taken WHOLESALE, never mixed with local files: a clip
      // animates translation+scale in the DONOR body's proportions, so a
      // remote mesh driven by a local walk from another generation warps
      // (that's how Cosmina got bent). A row missing its walk simply stands.
      // No row at all = the local files keep working, exactly as today.
      const rowById = new Map(userModels.filter((m) => m.rigged).map((m) => [m.avatarId, m]));
      let remoted = 0;
      /* ⚠ THE MERGE ITSELF NOW LIVES IN `services/bodyRigs`. It was inline here
         and nowhere else, so the street got the current bodies while every room
         kept mounting roster.json's local paths — Khari, 2026-08-31: *"the placed
         residents in the rooms… are using the older 3D models instead of the
         newer ones."* Measured: 8 of 14 local bodies were a generation behind.
         One rule, four callers. */
      const resolved = merged.map((n) => {
        const m = rowById.get(n.avatarId);
        if (m?.rigged) remoted++;
        return withRemoteBody(n, m);
      });
      if (remoted) console.log(`[NPC] ${remoted}/${merged.length} roster bod${remoted === 1 ? 'y' : 'ies'} served from GCS`);

      if (!cancelled) { setNpcs(resolved); setNpcList([...resolved, ...extras]); }
    })();
    return () => { cancelled = true; };
  }, [setNpcList, bodiesTry]); // eslint-disable-line react-hooks/exhaustive-deps

  const placedBySlug = useMemo(
    () => new Map((placed || []).filter((p) => p.zone_id === 'street' && p.kind === 'resident').map((p) => [p.ref, p])),
    [placed],
  );

  if (!npcs.length || cityLive) return null;
  // Roster entries marked `test: true` are A/B bodies for comparing pipeline
  // output side by side (same plates, different reconstructor). They are not
  // residents and must never show up in a normal session — ?test=1 opts in.
  const visible = npcs.filter((n) => SHOW_TEST || !n.test)
    // Saved placement wins over the roster default, applied HERE at render
    // rather than in the load effect — the panel patches store state as you
    // nudge, and this is what turns that into the body actually moving.
    .map((n) => {
      const p = placedBySlug.get(n.slug);
      return p ? { ...n, post: p.pos, rotationY: p.rotY, height: p.size } : n;
    });
  return (
    <Suspense fallback={null}>
      {placing && <CameraBasisProbe />}
      {/* One NPC per boundary. Roster entries can point at assets the pipeline
          is STILL RENDERING (rigged + walk exported, idle not yet), and a
          missing GLB throws inside the loader — unwrapped, that one entry
          unmounts the whole Canvas and drops the WebGL context. Retrying lets
          an avatar appear by itself once its clips finish. */}
      {visible.map((n) => (
        <SafeMount
          key={n.avatarId ?? n.slug}
          label={`NPC ${n.name || n.slug}`}
          retryMs={30000}
          onRetry={() => useGLTF.clear([n.rigged, n.walk, n.idle].filter(Boolean))}
        >
          <Npc npc={n} />
        </SafeMount>
      ))}
    </Suspense>
  );
}
